/**
 * Операционный дашборд администратора на маршруте `/admin` (Фаза 7, И1).
 *
 * Защищён двойным гейтом: auth (`RequireAuth`) + `role==='admin'` (см. App.tsx —
 * не-админ редиректится на `/`; здесь — дублирующая проверка через useAuth для
 * самодостаточности/тестируемости). Backend `/api/admin/*` дополнительно закрыт
 * `RequireAdmin`, так что даже при подмене клиента не-админ получает 403.
 *
 * И1 — только read-only вкладка «Пользователи»: список с фильтром (поиск q,
 * роль, блокировка) и пагинацией + профиль пользователя со статистикой
 * (gamesCount/hostedSessions/totalPlayers/totalScore). Каркас вкладок готовит
 * задел под И3–И6 (Игры/Сессии/Витрины/Аудит/Лимиты) — пока disabled («скоро»).
 *
 * 403 (сессия перестала быть админской / cookie у"user'a) трактуется как
 * «недостаточно прав» и не валит весь экран — даём понятное сообщение.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  encodeDraftQuiz,
  parseWireQuizPayload,
  wireQuizToDraft,
  adminBlockUser,
  adminDeleteGame,
  adminDeleteUpload,
  adminGetLimits,
  adminGetShowcase,
  adminGetUser,
  adminListActiveSessions,
  adminListAudit,
  adminListGames,
  adminListUploads,
  adminListUsers,
  adminLogoutAll,
  adminSetUserRole,
  adminStopSession,
  adminUnblockUser,
  adminUpdateLimits,
  adminUpdateShowcase,
  adminVerifyUser,
  AUDIT_ACTION,
  LIMIT_KEYS,
  type AdminAuditItem,
  type AdminGameItem,
  type AdminLimitItem,
  type AdminListAuditParams,
  type AdminListGamesParams,
  type AdminListUploadsParams,
  type AdminListUsersParams,
  type AdminLimitsResp,
  type AdminRole,
  type AdminRoomInfo,
  type AdminUploadItem,
  type AdminUserItem,
  type AdminUserProfile,
} from '../api';
import { Link } from '../components/ui';
import { redirect } from '../router';
import { useAuth } from '../useAuth';
import { errorMessage, formatBytes } from '../util';
import { QuizQuestionList } from '../components/quiz';
import type { QuizDraft } from '../drafts';

/** Размер страницы списка пользователей (пагинация по offset). */
const PAGE_SIZE = 20;

/**
 * Вкладка дашборда. Активны (И1/И2/И3): users, games, sessions, audit; showcase/limits —
 * задел под И4/И6 (рисуются disabled «скоро»).
 */
type AdminTab = 'users' | 'games' | 'sessions' | 'showcase' | 'audit' | 'uploads' | 'limits';

/** Значения фильтра по блокировке (трёхзначная логика → adminListUsers.blocked). */
type BlockedFilter = 'all' | 'blocked' | 'active';

/** Значения фильтра по роли (`all` → query не отправляется). */
type RoleFilter = AdminRole | 'all';

/** Полный фильтр списка пользователей (управляет adminListUsers-параметрами). */
interface UsersFilter {
  q: string;
  role: RoleFilter;
  blocked: BlockedFilter;
}

const INITIAL_FILTER: UsersFilter = { q: '', role: 'all', blocked: 'all' };

/**
 * Список вкладок дашборда. `soon: true` — вкладка ещё не реализована (И4/И6):
 * рисуется как disabled с подсказкой «скоро», чтобы дать навигационный каркас.
 */
const TABS: ReadonlyArray<{ key: AdminTab; label: string; soon: boolean }> = [
  { key: 'users', label: 'admin.tabs.users', soon: false },
  { key: 'games', label: 'admin.tabs.games', soon: false },
  { key: 'sessions', label: 'admin.tabs.sessions', soon: false },
  // И4: редактор публичных витрин (Космос/Звёздные войны/Наука) активирован.
  { key: 'showcase', label: 'admin.tabs.showcase', soon: false },
  { key: 'audit', label: 'admin.tabs.audit', soon: false },
  // И5: модерация загруженных картинок (список/удаление, referenced/force).
  { key: 'uploads', label: 'admin.tabs.uploads', soon: false },
  // И6: конфигурируемые лимиты (просмотр/редактирование 9 значений) активирован.
  { key: 'limits', label: 'admin.tabs.limits', soon: false },
];

/**
 * Дружелюбное сообщение об ошибке admin-вызова по статусу. 403 — недостаточно
 * прав (сессия не админ); 404 — пользователь не найден; иное — сырое сообщение.
 */
function adminErrorMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return t('admin.forbidden');
    if (e.status === 404) return t('admin.users.notFound');
  }
  return errorMessage(e);
}

/**
 * Сообщение об ошибке admin-мутации (И2). Мутации отдают 409 со структурированным
 * кодом в теле `{"error":"<код>"}` (см. errCode* в [admin.go]); `request` кладёт
 * этот код в ApiError.message. Здесь код маппится в человекочитаемый i18n:
 *   - cannot_block_self — нельзя заблокировать себя;
 *   - cannot_revoke_self — нельзя снять админ-роль с себя;
 *   - last_admin — нельзя оставить систему без единственного админа (RA1).
 * Прочие статусы — как в adminErrorMessage (403/404); сеть/неизвестное — сырое.
 */
function adminActionError(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError && e.status === 409) {
    switch (e.message) {
      case 'cannot_block_self':
        return t('admin.errors.cannot_block_self');
      case 'cannot_revoke_self':
        return t('admin.errors.cannot_revoke_self');
      case 'last_admin':
        return t('admin.errors.last_admin');
      default:
        return t('admin.errors.conflict');
    }
  }
  if (e instanceof ApiError && (e.status === 403 || e.status === 404 || e.status >= 500)) {
    return adminErrorMessage(t, e);
  }
  return errorMessage(e);
}

/** Локализованная дата/время из ISO-строки (createdAt/updatedAt/...). */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** i18n-ключ человекочитаемой метки действия аудита по его коду (admin_actions.action). */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  [AUDIT_ACTION.block]: 'admin.audit.action.block',
  [AUDIT_ACTION.unblock]: 'admin.audit.action.unblock',
  [AUDIT_ACTION.roleGrant]: 'admin.audit.action.roleGrant',
  [AUDIT_ACTION.roleRevoke]: 'admin.audit.action.roleRevoke',
  [AUDIT_ACTION.verify]: 'admin.audit.action.verify',
  [AUDIT_ACTION.logoutAll]: 'admin.audit.action.logoutAll',
  // И4: правка контента витрины (Космос/Звёздные войны/Наука и т.п.).
  [AUDIT_ACTION.showcaseUpdate]: 'admin.audit.action.showcaseUpdate',
  // И5: удаление файла картинки из UPLOADS_DIR.
  [AUDIT_ACTION.uploadDelete]: 'admin.audit.action.uploadDelete',
  // И6: правка конфигурируемых лимитов (PUT /api/admin/limits).
  [AUDIT_ACTION.limitsUpdate]: 'admin.audit.action.limitsUpdate',
};

/**
 * Человекочитаемая метка действия аудита через i18n-маппинг. Неизвестный код
 * (будущие И3+: game.delete/showcase.update/limits.update) выводится как есть,
 * чтобы журнал оставался читаемым без правки фронта.
 */
function auditActionLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  action: string,
): string {
  const key = AUDIT_ACTION_LABEL[action];
  return key ? t(key) : action;
}

/**
 * Краткое описание meta-деталей записи аудита (причина/роль/число сессий). meta
 * приходит встроенным JSON-объектом (см. metaReason/metaRoleChange/metaLogoutAll в
 * store/admin_mutations.go). Парсится безопасно: при отсутствии/нечитаемом — ''.
 */
function auditMetaText(
  t: (key: string, opts?: Record<string, unknown>) => string,
  action: string,
  meta: Record<string, unknown> | undefined,
): string {
  if (!meta) return '';
  const reason = typeof meta.reason === 'string' ? meta.reason : '';
  const deleted = typeof meta.deleted === 'number' ? meta.deleted : null;
  const role = typeof meta.role === 'string' ? meta.role : '';
  // И4: meta правки витрины — {from,to} (старое/новое название темы).
  const from = typeof meta.from === 'string' ? meta.from : '';
  const to = typeof meta.to === 'string' ? meta.to : '';
  if (action === AUDIT_ACTION.block && reason.length > 0) {
    return t('admin.audit.metaReason', { reason });
  }
  if (action === AUDIT_ACTION.logoutAll && deleted !== null) {
    return t('admin.audit.metaDeleted', { n: deleted });
  }
  if (
    (action === AUDIT_ACTION.roleGrant || action === AUDIT_ACTION.roleRevoke) &&
    role.length > 0
  ) {
    return t('admin.audit.metaRole', { role });
  }
  if (action === AUDIT_ACTION.showcaseUpdate && (from.length > 0 || to.length > 0)) {
    return t('admin.audit.metaShowcase', { from, to });
  }
  return '';
}

export function Admin(): JSX.Element {
  const { t } = useTranslation();
  const { user, loading } = useAuth();

  const [tab, setTab] = useState<AdminTab>('users');

  // ── Список пользователей ────────────────────────────────────────────────
  const [filter, setFilter] = useState<UsersFilter>(INITIAL_FILTER);
  // Поле ввода поиска отделено от applied-фильтра: применяется по submit формы
  // (Enter / кнопка «Искать»), чтобы не дёргать сервер на каждое нажатие.
  const [searchInput, setSearchInput] = useState('');
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // ── Профиль выбранного пользователя ─────────────────────────────────────
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [profile, setProfile] = useState<AdminUserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // ── Действия модерации в профиле (И2): block/unblock/role/verify/logout-all.
  // actionLoading — ключ выполняемого действия (для disable всех кнопок во время
  // запроса); actionError/actionNotice — inline-сообщения об ошибке/успехе.
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  // Дублирующий гейт (как в Dashboard для /me): не-админ → главная. App уже
  // редиректит, но экран самодостаточен и тестируем вне диспетчера.
  const isAdmin = user?.role === 'admin';
  useEffect(() => {
    if (!loading && !isAdmin) redirect('/');
  }, [loading, isAdmin]);

  const loadUsers = useCallback(
    async (f: UsersFilter, off: number): Promise<void> => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const params: AdminListUsersParams = { limit: PAGE_SIZE, offset: off };
        const q = f.q.trim();
        if (q.length > 0) params.q = q;
        if (f.role !== 'all') params.role = f.role;
        if (f.blocked !== 'all') params.blocked = f.blocked === 'blocked';
        setUsers(await adminListUsers(params));
      } catch (e) {
        setUsersError(adminErrorMessage(t, e));
      } finally {
        setUsersLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (isAdmin) void loadUsers(filter, offset);
  }, [loadUsers, filter, offset, isAdmin]);

  function handleSearchSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setOffset(0);
    setFilter((prev) => ({ ...prev, q: searchInput }));
  }

  function handleRoleChange(role: RoleFilter): void {
    setOffset(0);
    setFilter((prev) => ({ ...prev, role }));
  }

  function handleBlockedChange(blocked: BlockedFilter): void {
    setOffset(0);
    setFilter((prev) => ({ ...prev, blocked }));
  }

  async function handleSelectUser(id: number): Promise<void> {
    setSelectedId(id);
    setProfile(null);
    setProfileError(null);
    setActionError(null);
    setActionNotice(null);
    setProfileLoading(true);
    try {
      setProfile(await adminGetUser(id));
    } catch (e) {
      setProfileError(adminErrorMessage(t, e));
    } finally {
      setProfileLoading(false);
    }
  }

  function handleProfileBack(): void {
    setSelectedId(null);
    setProfile(null);
    setProfileError(null);
    setActionError(null);
    setActionNotice(null);
  }

  // ID текущего админа (для признака self — backend и так защищён гвардом 409,
  // UI дополнительно подсвечивает собственный аккаунт в панели действий).
  const currentUserId = user?.id;

  /**
   * Выполняет мутацию аккаунта (block/unblock/role/verify), мерджит обновлённый DTO
   * в профиль (без отдельного GET — сервер уже вернул актуальные поля) и показывает
   * inline-уведомление. 409-гварды (cannot_block_self / cannot_revoke_self / last_admin)
   * → человекочитаемое сообщение через adminActionError; сеть/403/404 — adminErrorMessage.
   */
  async function runMutation(
    key: string,
    fn: () => Promise<AdminUserItem>,
    successNotice: string,
  ): Promise<void> {
    setActionLoading(key);
    setActionError(null);
    setActionNotice(null);
    try {
      const u = await fn();
      setProfile((prev) => (prev ? { ...prev, ...u } : prev));
      setActionNotice(successNotice);
    } catch (e) {
      setActionError(adminActionError(t, e));
    } finally {
      setActionLoading(null);
    }
  }

  // Блокировка — деструктивная: reason через prompt + подтверждение через confirm.
  async function handleBlock(): Promise<void> {
    if (!profile) return;
    const reason = window.prompt(t('admin.actions.reasonPrompt'), '');
    if (reason === null) return; // отменено.
    if (!window.confirm(t('admin.actions.confirmBlock', { user: profile.email }))) return;
    await runMutation(
      'block',
      () => adminBlockUser(profile.id, reason),
      t('admin.actions.blocked'),
    );
  }

  // Разблокировка — безопасная (без подтверждения).
  async function handleUnblock(): Promise<void> {
    if (!profile) return;
    await runMutation('unblock', () => adminUnblockUser(profile.id), t('admin.actions.unblocked'));
  }

  async function handleMakeAdmin(): Promise<void> {
    if (!profile) return;
    if (!window.confirm(t('admin.actions.confirmMakeAdmin', { user: profile.email }))) return;
    await runMutation(
      'role',
      () => adminSetUserRole(profile.id, 'admin'),
      t('admin.actions.roleGranted'),
    );
  }

  // Снятие админ-роли — деструктивное (подтверждение); backend вернёт 409
  // cannot_revoke_self (с себя) или last_admin (единственный админ).
  async function handleRemoveAdmin(): Promise<void> {
    if (!profile) return;
    if (!window.confirm(t('admin.actions.confirmRemoveAdmin', { user: profile.email }))) return;
    await runMutation(
      'role',
      () => adminSetUserRole(profile.id, 'user'),
      t('admin.actions.roleRevoked'),
    );
  }

  // Ручная верификация — безопасная (поддержка при потере токена).
  async function handleVerify(): Promise<void> {
    if (!profile) return;
    await runMutation('verify', () => adminVerifyUser(profile.id), t('admin.actions.verified'));
  }

  // Logout-all — деструктивное (подтверждение); показывает число отозванных сессий.
  async function handleLogoutAll(): Promise<void> {
    if (!profile) return;
    if (!window.confirm(t('admin.actions.confirmLogoutAll', { user: profile.email }))) return;
    setActionLoading('logoutAll');
    setActionError(null);
    setActionNotice(null);
    try {
      const res = await adminLogoutAll(profile.id);
      setProfile((prev) => (prev ? { ...prev, ...res } : prev));
      setActionNotice(t('admin.actions.sessionsRevoked', { n: res.sessionsDeleted }));
    } catch (e) {
      setActionError(adminActionError(t, e));
    } finally {
      setActionLoading(null);
    }
  }

  // Пока идёт bootstrap либо пользователь не админ — заглушка (редирект в
  // эффекте выше; сообщение выбираем по причине: loading vs недостаточно прав).
  if (loading || !isAdmin) {
    return (
      <main className="card card--page">
        <p className="muted" data-testid="admin-gate">
          {loading ? t('common.loading') : t('admin.forbidden')}
        </p>
      </main>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  // Нет общего total (backend отдаёт только страницу) → «Вперёд» доступна,
  // пока пришла полная страница (значит, возможны ещё записи).
  const hasNext = users.length === PAGE_SIZE;

  return (
    <main className="card card--wide" aria-labelledby="admin-title" data-testid="admin">
      <header className="screen-head">
        <h2 id="admin-title" className="block-title">
          {t('admin.title')}
        </h2>
        <div className="screen-head-meta">
          <Link to="/" className="btn btn--ghost">
            {t('nav.toMain')}
          </Link>
        </div>
      </header>

      <nav className="admin-tabs" role="tablist" aria-label={t('admin.title')}>
        {TABS.map((tb) => {
          const active = tb.key === tab;
          const commonCls = `admin-tab${active ? ' admin-tab--active' : ''}${
            tb.soon ? ' admin-tab--soon' : ''
          }`;
          if (tb.soon) {
            return (
              <button
                key={tb.key}
                type="button"
                role="tab"
                aria-selected={active}
                aria-disabled="true"
                disabled
                className={commonCls}
                title={t('admin.comingSoon')}
                data-testid={`admin-tab-${tb.key}`}
              >
                {t(tb.label)}
                <span className="admin-tab__badge" aria-hidden="true">
                  {t('admin.comingSoon')}
                </span>
              </button>
            );
          }
          return (
            <button
              key={tb.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={commonCls}
              onClick={() => setTab(tb.key)}
              data-testid={`admin-tab-${tb.key}`}
            >
              {t(tb.label)}
            </button>
          );
        })}
      </nav>

      {tab === 'games' ? (
        <GamesView />
      ) : tab === 'sessions' ? (
        <SessionsView />
      ) : tab === 'showcase' ? (
        <ShowcaseView />
      ) : tab === 'audit' ? (
        <AuditView />
      ) : tab === 'uploads' ? (
        <UploadsView />
      ) : tab === 'limits' ? (
        <LimitsView />
      ) : selectedId !== null ? (
        <ProfileView
          profile={profile}
          loading={profileLoading}
          error={profileError}
          currentUserId={currentUserId}
          actionLoading={actionLoading}
          actionError={actionError}
          actionNotice={actionNotice}
          onBack={handleProfileBack}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          onMakeAdmin={handleMakeAdmin}
          onRemoveAdmin={handleRemoveAdmin}
          onVerify={handleVerify}
          onLogoutAll={handleLogoutAll}
        />
      ) : (
        <section className="block" aria-labelledby="admin-users-title">
          <div className="block-head">
            <h3 id="admin-users-title" className="block-subtitle">
              {t('admin.users.title')}
            </h3>
            <button
              type="button"
              className="link-btn"
              onClick={() => void loadUsers(filter, offset)}
              disabled={usersLoading}
            >
              {t('common.refresh')}
            </button>
          </div>

          <form className="admin-filters" onSubmit={handleSearchSubmit}>
            <input
              type="search"
              className="admin-filters__search"
              placeholder={t('admin.users.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={t('admin.users.searchPlaceholder')}
            />
            <select
              className="admin-filters__select"
              value={filter.role}
              onChange={(e) => handleRoleChange(e.target.value as RoleFilter)}
              aria-label={t('admin.users.roleLabel')}
            >
              <option value="all">{t('admin.users.roleAll')}</option>
              <option value="admin">{t('admin.users.roleAdmin')}</option>
              <option value="user">{t('admin.users.roleUser')}</option>
            </select>
            <select
              className="admin-filters__select"
              value={filter.blocked}
              onChange={(e) => handleBlockedChange(e.target.value as BlockedFilter)}
              aria-label={t('admin.users.blockedLabel')}
            >
              <option value="all">{t('admin.users.blockedAll')}</option>
              <option value="blocked">{t('admin.users.blockedOnly')}</option>
              <option value="active">{t('admin.users.blockedActive')}</option>
            </select>
            <button type="submit" className="btn" disabled={usersLoading}>
              {t('admin.users.searchButton')}
            </button>
          </form>

          {usersLoading ? (
            <p className="muted" data-testid="admin-users-loading">
              {t('admin.users.loading')}
            </p>
          ) : usersError ? (
            <p className="error" role="alert" data-testid="admin-users-error">
              {usersError}
            </p>
          ) : users.length === 0 ? (
            <p className="muted" data-testid="admin-users-empty">
              {t('admin.users.empty')}
            </p>
          ) : (
            <div className="admin-table-wrap" data-testid="admin-users-table">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.users.colId')}</th>
                    <th>{t('admin.users.colEmail')}</th>
                    <th>{t('admin.users.colUsername')}</th>
                    <th>{t('admin.users.colRole')}</th>
                    <th>{t('admin.users.colStatus')}</th>
                    <th>{t('admin.users.colCreated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="admin-table__row"
                      onClick={() => void handleSelectUser(u.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          void handleSelectUser(u.id);
                        }
                      }}
                      tabIndex={0}
                      data-testid="admin-user-row"
                    >
                      <td className="admin-table__id">{u.id}</td>
                      <td className="admin-table__email">{u.email}</td>
                      <td className="admin-table__username">{u.username}</td>
                      <td>
                        <span
                          className={`pill admin-role${
                            u.role === 'admin' ? ' admin-role--admin' : ''
                          }`}
                          data-testid={`admin-user-role-${u.role}`}
                        >
                          {u.role === 'admin' ? t('admin.role.admin') : t('admin.role.user')}
                        </span>
                      </td>
                      <td className="admin-table__status">
                        {u.isBlocked ? (
                          <span className="pill pill--unverified">{t('admin.status.blocked')}</span>
                        ) : u.verified ? (
                          <span className="pill pill--verified">{t('admin.status.verified')}</span>
                        ) : (
                          <span className="pill pill--unverified">
                            {t('admin.status.unverified')}
                          </span>
                        )}
                      </td>
                      <td className="admin-table__created">{formatDate(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!usersLoading && !usersError && users.length > 0 ? (
            <div className="admin-pager" data-testid="admin-pager">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={!hasPrev}
                data-testid="admin-prev"
              >
                {t('admin.users.prev')}
              </button>
              <span className="admin-pager__info">{t('admin.users.pageInfo', { page })}</span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasNext}
                data-testid="admin-next"
              >
                {t('admin.users.next')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </main>
  );
}

// ─── Профиль пользователя (детали + статистика) ──────────────────────────────

interface ProfileViewProps {
  profile: AdminUserProfile | null;
  loading: boolean;
  error: string | null;
  /** ID текущего админа (для self-подсветки в панели действий). */
  currentUserId: number | undefined;
  /** Ключ выполняемого действия (null — ничего не выполняется); блокирует все кнопки. */
  actionLoading: string | null;
  /** Inline-сообщение об ошибке мутации (напр. 409 cannot_revoke_self / last_admin). */
  actionError: string | null;
  /** Inline-уведомление об успехе мутации (заблокирован / отозвано сессий: N / …). */
  actionNotice: string | null;
  onBack: () => void;
  onBlock: () => void;
  onUnblock: () => void;
  onMakeAdmin: () => void;
  onRemoveAdmin: () => void;
  onVerify: () => void;
  onLogoutAll: () => void;
}

function ProfileView({
  profile,
  loading,
  error,
  currentUserId,
  actionLoading,
  actionError,
  actionNotice,
  onBack,
  onBlock,
  onUnblock,
  onMakeAdmin,
  onRemoveAdmin,
  onVerify,
  onLogoutAll,
}: ProfileViewProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <section className="block" aria-labelledby="admin-profile-title" data-testid="admin-profile">
      <div className="block-head">
        <h3 id="admin-profile-title" className="block-subtitle">
          {t('admin.profile.title')}
        </h3>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onBack}
          data-testid="admin-profile-back"
        >
          {t('admin.profile.back')}
        </button>
      </div>

      {loading ? (
        <p className="muted" data-testid="admin-profile-loading">
          {t('admin.users.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-profile-error">
          {error}
        </p>
      ) : profile ? (
        <>
          <dl className="admin-fields" data-testid="admin-profile-fields">
            <div className="admin-fields__row">
              <dt>{t('admin.profile.id')}</dt>
              <dd>{profile.id}</dd>
            </div>
            <div className="admin-fields__row">
              <dt>{t('admin.profile.email')}</dt>
              <dd>{profile.email}</dd>
            </div>
            <div className="admin-fields__row">
              <dt>{t('admin.profile.username')}</dt>
              <dd>{profile.username}</dd>
            </div>
            {profile.fullName ? (
              <div className="admin-fields__row">
                <dt>{t('admin.profile.fullName')}</dt>
                <dd>{profile.fullName}</dd>
              </div>
            ) : null}
            <div className="admin-fields__row">
              <dt>{t('admin.profile.role')}</dt>
              <dd>
                <span
                  className={`pill admin-role${
                    profile.role === 'admin' ? ' admin-role--admin' : ''
                  }`}
                >
                  {profile.role === 'admin' ? t('admin.role.admin') : t('admin.role.user')}
                </span>
              </dd>
            </div>
            <div className="admin-fields__row">
              <dt>{t('admin.profile.status')}</dt>
              <dd>
                {profile.isBlocked ? (
                  <span className="pill pill--unverified">{t('admin.status.blocked')}</span>
                ) : profile.verified ? (
                  <span className="pill pill--verified">{t('admin.status.verified')}</span>
                ) : (
                  <span className="pill pill--unverified">{t('admin.status.unverified')}</span>
                )}
              </dd>
            </div>
            {profile.isBlocked && profile.blockedReason ? (
              <div className="admin-fields__row">
                <dt>{t('admin.profile.blockedReason')}</dt>
                <dd>{profile.blockedReason}</dd>
              </div>
            ) : null}
            <div className="admin-fields__row">
              <dt>{t('admin.profile.created')}</dt>
              <dd>{formatDate(profile.createdAt)}</dd>
            </div>
            <div className="admin-fields__row">
              <dt>{t('admin.profile.updated')}</dt>
              <dd>{formatDate(profile.updatedAt)}</dd>
            </div>
          </dl>

          <div className="admin-stats" data-testid="admin-profile-stats">
            <StatCard label={t('admin.stats.gamesCount')} value={profile.gamesCount} />
            <StatCard label={t('admin.stats.hostedSessions')} value={profile.hostedSessions} />
            <StatCard label={t('admin.stats.totalPlayers')} value={profile.totalPlayers} />
            <StatCard label={t('admin.stats.totalScore')} value={profile.totalScore} />
          </div>

          <ActionsPanel
            profile={profile}
            isSelf={currentUserId !== undefined && profile.id === currentUserId}
            actionLoading={actionLoading}
            actionError={actionError}
            actionNotice={actionNotice}
            onBlock={onBlock}
            onUnblock={onUnblock}
            onMakeAdmin={onMakeAdmin}
            onRemoveAdmin={onRemoveAdmin}
            onVerify={onVerify}
            onLogoutAll={onLogoutAll}
          />
        </>
      ) : null}
    </section>
  );
}

interface StatCardProps {
  label: string;
  value: number;
}

function StatCard({ label, value }: StatCardProps): JSX.Element {
  return (
    <div className="admin-stat" data-testid="admin-stat">
      <span className="admin-stat__value">{value}</span>
      <span className="admin-stat__label">{label}</span>
    </div>
  );
}

// ─── Панель действий модерации (И2) ──────────────────────────────────────────

interface ActionsPanelProps {
  profile: AdminUserProfile;
  isSelf: boolean;
  actionLoading: string | null;
  actionError: string | null;
  actionNotice: string | null;
  onBlock: () => void;
  onUnblock: () => void;
  onMakeAdmin: () => void;
  onRemoveAdmin: () => void;
  onVerify: () => void;
  onLogoutAll: () => void;
}

/**
 * Кнопки block/unblock, grant/revoke admin, manual verify, logout-all. Состояние
 * кнопок зависит от текущего профиля (заблокирован? админ? верифицирован?).
 * Деструктивные (block/revoke/logoutAll) требуют подтверждения в caller'е (window.confirm).
 *
 * Гварды RA1 (нельзя блокировать/снять роль с себя, нельзя оставить систему без
 * админа) проверяются backend'ом → 409 с кодом; UI НЕ дизейблит self-кнопки, чтобы
 * показать человекочитаемое 409-сообщение (adminActionError). Для собственного
 * аккаунта показывается подсказка (selfHint); все кнопки отключаются на время
 * любого запроса (actionLoading!==null).
 */
function ActionsPanel({
  profile,
  isSelf,
  actionLoading,
  actionError,
  actionNotice,
  onBlock,
  onUnblock,
  onMakeAdmin,
  onRemoveAdmin,
  onVerify,
  onLogoutAll,
}: ActionsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const busy = actionLoading !== null;
  return (
    <div className="admin-actions" data-testid="admin-profile-actions">
      <h4 className="block-subtitle">{t('admin.actions.title')}</h4>
      {isSelf ? <p className="muted admin-actions__self">{t('admin.actions.selfHint')}</p> : null}
      <div className="btn-row admin-actions__row">
        {profile.isBlocked ? (
          <button
            type="button"
            className="btn"
            onClick={onUnblock}
            disabled={busy}
            data-testid="admin-action-unblock"
          >
            {actionLoading === 'unblock'
              ? t('admin.actions.inProgress')
              : t('admin.actions.unblock')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--danger"
            onClick={onBlock}
            disabled={busy}
            data-testid="admin-action-block"
          >
            {actionLoading === 'block' ? t('admin.actions.inProgress') : t('admin.actions.block')}
          </button>
        )}
        {profile.role === 'admin' ? (
          <button
            type="button"
            className="btn btn--danger"
            onClick={onRemoveAdmin}
            disabled={busy}
            data-testid="admin-action-remove-admin"
          >
            {actionLoading === 'role'
              ? t('admin.actions.inProgress')
              : t('admin.actions.removeAdmin')}
          </button>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={onMakeAdmin}
            disabled={busy}
            data-testid="admin-action-make-admin"
          >
            {actionLoading === 'role'
              ? t('admin.actions.inProgress')
              : t('admin.actions.makeAdmin')}
          </button>
        )}
        {!profile.verified ? (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onVerify}
            disabled={busy}
            data-testid="admin-action-verify"
          >
            {actionLoading === 'verify' ? t('admin.actions.inProgress') : t('admin.actions.verify')}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--danger"
          onClick={onLogoutAll}
          disabled={busy}
          data-testid="admin-action-logout-all"
        >
          {actionLoading === 'logoutAll'
            ? t('admin.actions.inProgress')
            : t('admin.actions.logoutAll')}
        </button>
      </div>
      {actionError ? (
        <p className="error" role="alert" data-testid="admin-action-error">
          {actionError}
        </p>
      ) : null}
      {actionNotice ? (
        <p className="muted" role="status" data-testid="admin-action-notice">
          {actionNotice}
        </p>
      ) : null}
    </div>
  );
}

// ─── Вкладка «Аудит» (И2, read-only) ─────────────────────────────────────────

/** Размер страницы аудита (пагинация по offset). */
const AUDIT_PAGE_SIZE = 20;

/** Параметры фильтра аудита, вводимые пользователем. */
interface AuditFilter {
  action: string;
  actor: string;
  target: string;
}

/** Варианты action-фильтра в <select> (код → i18n-метка). */
const AUDIT_ACTION_OPTIONS: ReadonlyArray<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'admin.audit.filterActionAll' },
  { value: AUDIT_ACTION.block, labelKey: 'admin.audit.action.block' },
  { value: AUDIT_ACTION.unblock, labelKey: 'admin.audit.action.unblock' },
  { value: AUDIT_ACTION.roleGrant, labelKey: 'admin.audit.action.roleGrant' },
  { value: AUDIT_ACTION.roleRevoke, labelKey: 'admin.audit.action.roleRevoke' },
  { value: AUDIT_ACTION.verify, labelKey: 'admin.audit.action.verify' },
  { value: AUDIT_ACTION.logoutAll, labelKey: 'admin.audit.action.logoutAll' },
  // И4: правка контента витрины.
  { value: AUDIT_ACTION.showcaseUpdate, labelKey: 'admin.audit.action.showcaseUpdate' },
  // И5: модерация картинок — удаление файла из UPLOADS_DIR.
  { value: AUDIT_ACTION.uploadDelete, labelKey: 'admin.audit.action.uploadDelete' },
];

/**
 * Самодостаточный экран аудита: фильтры (action/actor/target), таблица записей
 * (дата, исполнитель, действие, объект, детали), пагинация. Загружает
 * GET /api/admin/audit при монтировании/смене фильтра. 403 → adminErrorMessage
 * (недостаточно прав); иные ошибки — сырое сообщение. actor/target применяются
 * по сабмиту формы (Enter/«Применить»), action — сразу (селект).
 */
function AuditView(): JSX.Element {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AuditFilter>({ action: '', actor: '', target: '' });
  const [appliedFilter, setAppliedFilter] = useState<AuditFilter>({
    action: '',
    actor: '',
    target: '',
  });
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminAuditItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAudit = useCallback(
    async (f: AuditFilter, off: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params: AdminListAuditParams = { limit: AUDIT_PAGE_SIZE, offset: off };
        if (f.action) params.action = f.action;
        const actor = Number.parseInt(f.actor, 10);
        if (Number.isFinite(actor) && actor > 0) params.actor = actor;
        const target = Number.parseInt(f.target, 10);
        if (Number.isFinite(target) && target > 0) params.target = target;
        setItems(await adminListAudit(params));
      } catch (e) {
        setError(adminErrorMessage(t, e));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadAudit(appliedFilter, offset);
  }, [loadAudit, appliedFilter, offset]);

  function handleApply(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setOffset(0);
    setAppliedFilter({ ...filter });
  }

  function handleActionChange(action: string): void {
    setOffset(0);
    setFilter((prev) => ({ ...prev, action }));
    setAppliedFilter((prev) => ({ ...prev, action }));
  }

  function handleActorChange(actor: string): void {
    setFilter((prev) => ({ ...prev, actor }));
  }

  function handleTargetChange(target: string): void {
    setFilter((prev) => ({ ...prev, target }));
  }

  const page = Math.floor(offset / AUDIT_PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  const hasNext = items.length === AUDIT_PAGE_SIZE;

  return (
    <section className="block" aria-labelledby="admin-audit-title" data-testid="admin-audit">
      <div className="block-head">
        <div>
          <h3 id="admin-audit-title" className="block-subtitle">
            {t('admin.audit.title')}
          </h3>
          <p className="muted">{t('admin.audit.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => void loadAudit(appliedFilter, offset)}
          disabled={loading}
        >
          {t('common.refresh')}
        </button>
      </div>

      <form className="admin-filters" onSubmit={handleApply}>
        <select
          className="admin-filters__select"
          value={filter.action}
          onChange={(e) => handleActionChange(e.target.value)}
          aria-label={t('admin.audit.filterAction')}
          data-testid="admin-audit-filter-action"
        >
          {AUDIT_ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        <input
          type="search"
          inputMode="numeric"
          className="admin-filters__search"
          placeholder={t('admin.audit.filterActor')}
          value={filter.actor}
          onChange={(e) => handleActorChange(e.target.value)}
          aria-label={t('admin.audit.filterActor')}
          data-testid="admin-audit-filter-actor"
        />
        <input
          type="search"
          inputMode="numeric"
          className="admin-filters__search"
          placeholder={t('admin.audit.filterTarget')}
          value={filter.target}
          onChange={(e) => handleTargetChange(e.target.value)}
          aria-label={t('admin.audit.filterTarget')}
          data-testid="admin-audit-filter-target"
        />
        <button type="submit" className="btn" disabled={loading} data-testid="admin-audit-apply">
          {t('admin.audit.apply')}
        </button>
      </form>

      {loading ? (
        <p className="muted" data-testid="admin-audit-loading">
          {t('admin.audit.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-audit-error">
          {error}
        </p>
      ) : items.length === 0 ? (
        <p className="muted" data-testid="admin-audit-empty">
          {t('admin.audit.empty')}
        </p>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-audit-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.audit.colDate')}</th>
                <th>{t('admin.audit.colActor')}</th>
                <th>{t('admin.audit.colAction')}</th>
                <th>{t('admin.audit.colTarget')}</th>
                <th>{t('admin.audit.colMeta')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const meta = auditMetaText(t, a.action, a.meta);
                const targetTypeLabel =
                  a.targetType === 'user'
                    ? t('admin.audit.targetTypeUser')
                    : a.targetType === 'game'
                      ? t('admin.audit.targetTypeGame')
                      : a.targetType === 'upload'
                        ? t('admin.audit.targetTypeUpload')
                        : a.targetType === 'settings'
                          ? t('admin.audit.targetTypeSettings')
                          : a.targetType;
                return (
                  <tr key={a.id} data-testid="admin-audit-row">
                    <td className="admin-table__created">{formatDate(a.createdAt)}</td>
                    <td className="admin-table__id">
                      {t('admin.audit.actorId', { id: a.actorUserId })}
                    </td>
                    <td data-testid="admin-audit-action">{auditActionLabel(t, a.action)}</td>
                    <td>
                      {targetTypeLabel}
                      {a.targetId !== undefined
                        ? ` ${t('admin.audit.targetId', { id: a.targetId })}`
                        : ''}
                    </td>
                    <td className="admin-audit__meta">{meta}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && items.length > 0 ? (
        <div className="admin-pager" data-testid="admin-audit-pager">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setOffset(Math.max(0, offset - AUDIT_PAGE_SIZE))}
            disabled={!hasPrev}
            data-testid="admin-audit-prev"
          >
            {t('admin.audit.prev')}
          </button>
          <span className="admin-pager__info">{t('admin.audit.pageInfo', { page })}</span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setOffset(offset + AUDIT_PAGE_SIZE)}
            disabled={!hasNext}
            data-testid="admin-audit-next"
          >
            {t('admin.audit.next')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ─── Вкладка «Игры» (И3, дашборд игр с размерами + удаление) ──────────────────

/** Размер страницы списка игр (пагинация по offset). */
const GAMES_PAGE_SIZE = 20;

/** Полный фильтр списка игр (управляет adminListGames-параметрами). */
interface GamesFilter {
  q: string;
  type: string;
  owner: string;
}

const INITIAL_GAMES_FILTER: GamesFilter = { q: '', type: '', owner: '' };

/**
 * Локализованные единицы размера (Б/КБ/МБ/ГБ/ТБ) из i18n `common.bytes.*` для
 * formatBytes — массив memo-зависит только от `t` (пересоздаётся при смене языка).
 */
function useByteUnits(): string[] {
  const { t } = useTranslation();
  return useMemo(
    () => [
      t('common.bytes.B'),
      t('common.bytes.KB'),
      t('common.bytes.MB'),
      t('common.bytes.GB'),
      t('common.bytes.TB'),
    ],
    [t],
  );
}

/**
 * Сообщение об ошибке admin-вызова игр: 404 → «квиз не найден (возможно, уже удалён)»,
 * прочее (403/сеть) — как в adminErrorMessage.
 */
function adminGameErrorMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError && e.status === 404) return t('admin.games.notFound');
  return adminErrorMessage(t, e);
}

/**
 * Человекочитаемая ячейка владельца игры: email → ник → #id → «—» (для library-seed/
 * anon). Соответствует LEFT JOIN users на бэкенде (ownerEmail/ownerUsername опускаются
 * при NULL для library/anon/удалённого владельца).
 */
function gameOwnerCell(
  t: (key: string, opts?: Record<string, unknown>) => string,
  g: AdminGameItem,
): string {
  if (g.ownerEmail && g.ownerEmail.length > 0) return g.ownerEmail;
  if (g.ownerUsername && g.ownerUsername.length > 0) return g.ownerUsername;
  if (g.ownerUserId !== undefined) return `#${g.ownerUserId}`;
  return t('admin.games.noOwner');
}

/**
 * Самодостаточный экран игр: таблица всех квизов с размерами (payload + картинки),
 * фильтр (поиск по title, тип, владелец) + пагинация + удаление любого квиза
 * (admin override, без owner-check) с подтверждением. 404 при удалении (квиз уже
 * удалён) → дружелюбное сообщение; 403 (сессия перестала быть админской) — как users.
 * Размеры — человекочитаемо через formatBytes с локализованными единицами (useByteUnits).
 */
function GamesView(): JSX.Element {
  const { t } = useTranslation();
  const byteUnits = useByteUnits();
  const fmt = useCallback((n: number): string => formatBytes(n, byteUnits), [byteUnits]);

  const [filter, setFilter] = useState<GamesFilter>(INITIAL_GAMES_FILTER);
  // Поля ввода отделены от applied-фильтра: применяются по submit формы (Enter/кнопка),
  // чтобы не дёргать сервер на каждое нажатие. type применяется сразу (селект).
  const [searchInput, setSearchInput] = useState('');
  const [ownerInput, setOwnerInput] = useState('');
  const [offset, setOffset] = useState(0);

  const [games, setGames] = useState<AdminGameItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Удаление: deletingId — id удаляемого квиза (блокирует все delete-кнопки на время
  // запроса); actionError/actionNotice — inline-сообщения об ошибке/успехе.
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const loadGames = useCallback(
    async (f: GamesFilter, off: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params: AdminListGamesParams = { limit: GAMES_PAGE_SIZE, offset: off };
        const q = f.q.trim();
        if (q.length > 0) params.q = q;
        if (f.type.length > 0) params.type = f.type;
        const owner = Number.parseInt(f.owner, 10);
        if (Number.isFinite(owner) && owner > 0) params.owner = owner;
        setGames(await adminListGames(params));
      } catch (e) {
        setError(adminErrorMessage(t, e));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadGames(filter, offset);
  }, [loadGames, filter, offset]);

  function handleApply(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setOffset(0);
    setFilter({ q: searchInput, type: filter.type, owner: ownerInput });
  }

  function handleTypeChange(type: string): void {
    setOffset(0);
    setFilter((prev) => ({ ...prev, type }));
  }

  async function handleDelete(g: AdminGameItem): Promise<void> {
    if (!window.confirm(t('admin.games.confirmDelete', { title: g.title }))) return;
    setDeletingId(g.id);
    setActionError(null);
    setActionNotice(null);
    try {
      await adminDeleteGame(g.id);
      setActionNotice(t('admin.games.deleted', { title: g.title }));
      // Локальный рефреш: убираем строку без отдельного GET (audit game.delete уже
      // написан сервером). При 404 (кто-то уже удалил) — дружелюбная ошибка, список не трогаем.
      setGames((prev) => prev.filter((x) => x.id !== g.id));
    } catch (e) {
      setActionError(adminGameErrorMessage(t, e));
    } finally {
      setDeletingId(null);
    }
  }

  const page = Math.floor(offset / GAMES_PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  const hasNext = games.length === GAMES_PAGE_SIZE;

  return (
    <section className="block" aria-labelledby="admin-games-title" data-testid="admin-games">
      <div className="block-head">
        <div>
          <h3 id="admin-games-title" className="block-subtitle">
            {t('admin.games.title')}
          </h3>
          <p className="muted">{t('admin.games.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => void loadGames(filter, offset)}
          disabled={loading}
        >
          {t('common.refresh')}
        </button>
      </div>

      <form className="admin-filters" onSubmit={handleApply}>
        <input
          type="search"
          className="admin-filters__search"
          placeholder={t('admin.games.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label={t('admin.games.searchPlaceholder')}
          data-testid="admin-games-filter-q"
        />
        <select
          className="admin-filters__select"
          value={filter.type}
          onChange={(e) => handleTypeChange(e.target.value)}
          aria-label={t('admin.games.typeLabel')}
          data-testid="admin-games-filter-type"
        >
          <option value="">{t('admin.games.typeAll')}</option>
          <option value="quiz">{t('admin.games.typeQuiz')}</option>
          <option value="library">{t('admin.games.typeLibrary')}</option>
        </select>
        <input
          type="search"
          inputMode="numeric"
          className="admin-filters__search"
          placeholder={t('admin.games.ownerPlaceholder')}
          value={ownerInput}
          onChange={(e) => setOwnerInput(e.target.value)}
          aria-label={t('admin.games.ownerLabel')}
          data-testid="admin-games-filter-owner"
        />
        <button type="submit" className="btn" disabled={loading} data-testid="admin-games-apply">
          {t('admin.games.searchButton')}
        </button>
      </form>

      {actionError ? (
        <p className="error" role="alert" data-testid="admin-games-action-error">
          {actionError}
        </p>
      ) : null}
      {actionNotice ? (
        <p className="muted" role="status" data-testid="admin-games-action-notice">
          {actionNotice}
        </p>
      ) : null}

      {loading ? (
        <p className="muted" data-testid="admin-games-loading">
          {t('admin.games.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-games-error">
          {error}
        </p>
      ) : games.length === 0 ? (
        <p className="muted" data-testid="admin-games-empty">
          {t('admin.games.empty')}
        </p>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-games-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.games.colId')}</th>
                <th>{t('admin.games.colTitle')}</th>
                <th>{t('admin.games.colType')}</th>
                <th>{t('admin.games.colCategory')}</th>
                <th>{t('admin.games.colOwner')}</th>
                <th>{t('admin.games.colCreated')}</th>
                <th>{t('admin.games.colSize')}</th>
                <th>{t('admin.games.colImages')}</th>
                <th>{t('admin.games.colCount')}</th>
                <th className="admin-table__actions">{t('admin.games.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => (
                <tr key={g.id} data-testid="admin-game-row">
                  <td className="admin-table__id">{g.id}</td>
                  <td className="admin-table__email">{g.title}</td>
                  <td data-testid="admin-game-type">{g.type}</td>
                  <td>{g.category ?? '—'}</td>
                  <td data-testid="admin-game-owner">{gameOwnerCell(t, g)}</td>
                  <td className="admin-table__created">{formatDate(g.createdAt)}</td>
                  <td data-testid="admin-game-size">
                    <span>{fmt(g.totalBytes)}</span>
                    <span className="muted admin-games__size-sub">
                      {t('admin.games.sizeBreakdown', {
                        payload: fmt(g.payloadBytes),
                        images: fmt(g.imagesBytes),
                      })}
                    </span>
                  </td>
                  <td data-testid="admin-game-images">{fmt(g.imagesBytes)}</td>
                  <td data-testid="admin-game-count">{g.imageCount}</td>
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="link-btn link-btn--danger"
                      onClick={() => void handleDelete(g)}
                      disabled={deletingId !== null}
                      data-testid={`admin-game-delete-${g.id}`}
                    >
                      {deletingId === g.id ? t('admin.games.deleting') : t('admin.games.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && games.length > 0 ? (
        <div className="admin-pager" data-testid="admin-games-pager">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setOffset(Math.max(0, offset - GAMES_PAGE_SIZE))}
            disabled={!hasPrev}
            data-testid="admin-games-prev"
          >
            {t('admin.games.prev')}
          </button>
          <span className="admin-pager__info">{t('admin.games.pageInfo', { page })}</span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setOffset(offset + GAMES_PAGE_SIZE)}
            disabled={!hasNext}
            data-testid="admin-games-next"
          >
            {t('admin.games.next')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ─── Вкладка «Активные сессии» (И3, Hub.Snapshot + stop) ──────────────────────

/** Интервал авто-refresh списка активных сессий (мс). Низкая нагрузка (admin-дашборд). */
const SESSIONS_POLL_MS = 7000;

/** i18n-ключ метки фазы по коду hub.RoomInfo.Phase. Неизвестный код → разделитель. */
const PHASE_LABEL_KEYS: Record<string, string> = {
  lobby: 'admin.sessions.phase.lobby',
  question: 'admin.sessions.phase.question',
  reveal: 'admin.sessions.phase.reveal',
  leaderboard: 'admin.sessions.phase.leaderboard',
  finished: 'admin.sessions.phase.finished',
};

/**
 * Локализованная метка фазы комнаты (lobby/question/reveal/leaderboard/finished).
 * Неизвестный/будущий код → «—» (журнал остаётся читаемым без правки фронта).
 */
function phaseLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  phase: string,
): string {
  const key = PHASE_LABEL_KEYS[phase];
  return key ? t(key) : t('admin.sessions.phase.unknown');
}

/**
 * Сообщение об ошибке stop сессии: 404 → «уже завершена или не активна» (комната
 * закрылась между снимком и кликом), прочее — как adminErrorMessage.
 */
function adminSessionStopError(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError && e.status === 404) return t('admin.sessions.notActive');
  return adminErrorMessage(t, e);
}

/**
 * Самодостаточный экран активных сессий: снимок живых комнат (Hub.Snapshot через
 * GET /api/admin/sessions/active) с авто-refresh каждые ~7 c. Колонки: PIN, gameId,
 * sessionId, хост (#id или «аноним» для 0), playerCount, phase (локализованная),
 * startedAt. Stop — POST /stop с подтверждением → удаление строки + inline-сообщение;
 * 404 (сессия уже завершилась) — дружелюбное сообщение. Пустое состояние — «нет
 * активных сессий».
 *
 * Polling/cleanup: useEffect ставит setInterval и возвращает cleanup (clearInterval),
 * который гарантированно снимается на unmount/смене вкладки — иначе интервал продолжил
 * бы дёргать API и обновлять state демонтированного компонента (memory leak).
 */
function SessionsView(): JSX.Element {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<AdminRoomInfo[]>([]);
  // loading=true только до первой загрузки; на poll НЕ сбрасываем (иначе список
  // мигает каждые 7 c). «loading && пусто» → показываем спиннер; иначе — данные.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // stoppingId — id останавливаемой сессии (блокирует все stop-кнопки на запрос).
  const [stoppingId, setStoppingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const loadSessions = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setSessions(await adminListActiveSessions());
    } catch (e) {
      setError(adminErrorMessage(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadSessions();
    const id = window.setInterval(() => {
      void loadSessions();
    }, SESSIONS_POLL_MS);
    // Очистка интервала на unmount/смене вкладки (ESLint react-hooks/exhaustive-deps
    // удовлетворён: cleanup возвращён из того же эффекта, что и подписка).
    return () => window.clearInterval(id);
  }, [loadSessions]);

  async function handleStop(s: AdminRoomInfo): Promise<void> {
    if (!window.confirm(t('admin.sessions.confirmStop', { pin: s.pin }))) return;
    setStoppingId(s.sessionId);
    setActionError(null);
    setActionNotice(null);
    try {
      await adminStopSession(s.sessionId);
      setActionNotice(t('admin.sessions.stopped', { pin: s.pin }));
      // Локально убираем строку (комната уже закрыта hub.StopSession + finished_at).
      // 404 (кто-то/таймаут уже остановил) → дружелюбное сообщение.
      setSessions((prev) => prev.filter((x) => x.sessionId !== s.sessionId));
    } catch (e) {
      setActionError(adminSessionStopError(t, e));
    } finally {
      setStoppingId(null);
    }
  }

  return (
    <section className="block" aria-labelledby="admin-sessions-title" data-testid="admin-sessions">
      <div className="block-head">
        <div>
          <h3 id="admin-sessions-title" className="block-subtitle">
            {t('admin.sessions.title')}
          </h3>
          <p className="muted">{t('admin.sessions.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => void loadSessions()}
          disabled={loading && sessions.length === 0}
        >
          {t('common.refresh')}
        </button>
      </div>

      {actionError ? (
        <p className="error" role="alert" data-testid="admin-sessions-action-error">
          {actionError}
        </p>
      ) : null}
      {actionNotice ? (
        <p className="muted" role="status" data-testid="admin-sessions-action-notice">
          {actionNotice}
        </p>
      ) : null}

      {loading && sessions.length === 0 ? (
        <p className="muted" data-testid="admin-sessions-loading">
          {t('admin.sessions.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-sessions-error">
          {error}
        </p>
      ) : sessions.length === 0 ? (
        <p className="muted" data-testid="admin-sessions-empty">
          {t('admin.sessions.empty')}
        </p>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-sessions-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.sessions.colPin')}</th>
                <th>{t('admin.sessions.colGame')}</th>
                <th>{t('admin.sessions.colSession')}</th>
                <th>{t('admin.sessions.colHost')}</th>
                <th>{t('admin.sessions.colPlayers')}</th>
                <th>{t('admin.sessions.colPhase')}</th>
                <th>{t('admin.sessions.colStarted')}</th>
                <th className="admin-table__actions">{t('admin.sessions.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.sessionId} data-testid="admin-session-row">
                  <td data-testid="admin-session-pin">{s.pin}</td>
                  <td>{s.gameId}</td>
                  <td>{s.sessionId}</td>
                  <td>{s.hostUserId ? `#${s.hostUserId}` : t('admin.sessions.hostAnon')}</td>
                  <td data-testid="admin-session-players">{s.playerCount}</td>
                  <td data-testid="admin-session-phase">{phaseLabel(t, s.phase)}</td>
                  <td className="admin-table__created">{formatDate(s.startedAt)}</td>
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="link-btn link-btn--danger"
                      onClick={() => void handleStop(s)}
                      disabled={stoppingId !== null}
                      data-testid={`admin-session-stop-${s.sessionId}`}
                    >
                      {stoppingId === s.sessionId
                        ? t('admin.sessions.stopping')
                        : t('admin.sessions.stop')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── Вкладка «Витрины» (И4, редактор публичных showcase-игр) ────────────────────

/**
 * Сообщение об ошибке showcase-операции (И4). 409 not_showcase — игра перестала
 * быть витриной (или это личный квиз/библиотека вопросов — guard на сервере);
 * 400 — невалидный payload/пустой title; 404 — игра удалена; прочее (403/сеть) —
 * как в adminErrorMessage.
 */
function adminShowcaseError(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return t('admin.showcase.errors.not_showcase');
    if (e.status === 400) return t('admin.showcase.errors.invalid_payload');
    if (e.status === 404) return t('admin.showcase.notFound');
  }
  return adminErrorMessage(t, e);
}

/**
 * Самодостаточный экран витрин: список showcase-игр (title, размеры, кол-во
 * картинок) с переходом в inline-редактор (В2). Список — adminListGames
 * {type:'library', category:'showcase'} (И3); payload НЕ входит (тяжёлый), поэтому
 * «Редактировать» грузит его отдельным adminGetShowcase и открывает форму (title +
 * <textarea> JSON + «Проверить» + «Сохранить» через adminUpdateShowcase). 409/400/404
 * маппятся в дружелобные сообщения. Подход В2 (inline) выбран вместо переиспользования
 * HostEditor: getGame/PUT /api/games — owner-scoped и недоступны для showcase (owner=NULL),
 * а HostEditor — полноэкранный редактор с localStorage-черновиками (инвазивно для вкладки).
 */
function ShowcaseView(): JSX.Element {
  const { t } = useTranslation();
  const byteUnits = useByteUnits();
  const fmt = useCallback((n: number): string => formatBytes(n, byteUnits), [byteUnits]);

  const [games, setGames] = useState<AdminGameItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminGameItem | null>(null);

  const loadGames = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setGames(await adminListGames({ type: 'library', category: 'showcase' }));
    } catch (e) {
      setError(adminErrorMessage(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadGames();
  }, [loadGames]);

  if (editing !== null) {
    return (
      <ShowcaseEditor
        game={editing}
        onBack={() => {
          setEditing(null);
          void loadGames();
        }}
      />
    );
  }

  return (
    <section className="block" aria-labelledby="admin-showcase-title" data-testid="admin-showcase">
      <div className="block-head">
        <div>
          <h3 id="admin-showcase-title" className="block-subtitle">
            {t('admin.showcase.title')}
          </h3>
          <p className="muted">{t('admin.showcase.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => void loadGames()}
          disabled={loading}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading ? (
        <p className="muted" data-testid="admin-showcase-loading">
          {t('admin.showcase.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-showcase-error">
          {error}
        </p>
      ) : games.length === 0 ? (
        <p className="muted" data-testid="admin-showcase-empty">
          {t('admin.showcase.empty')}
        </p>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-showcase-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.showcase.colId')}</th>
                <th>{t('admin.showcase.colTitle')}</th>
                <th>{t('admin.showcase.colSize')}</th>
                <th>{t('admin.showcase.colImages')}</th>
                <th>{t('admin.showcase.colCreated')}</th>
                <th className="admin-table__actions">{t('admin.showcase.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => (
                <tr key={g.id} data-testid="admin-showcase-row">
                  <td className="admin-table__id">{g.id}</td>
                  <td className="admin-table__email" data-testid={`admin-showcase-title-${g.id}`}>
                    {g.title}
                  </td>
                  <td data-testid={`admin-showcase-size-${g.id}`}>{fmt(g.totalBytes)}</td>
                  <td data-testid={`admin-showcase-count-${g.id}`}>
                    {t('admin.showcase.imagesCount', {
                      n: g.imageCount,
                      bytes: fmt(g.imagesBytes),
                    })}
                  </td>
                  <td className="admin-table__created">{formatDate(g.createdAt)}</td>
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setEditing(g)}
                      data-testid={`admin-showcase-edit-${g.id}`}
                    >
                      {t('admin.showcase.edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface ShowcaseEditorProps {
  game: AdminGameItem;
  /** Выход к списку (с перезагрузкой, чтобы отразить сохранённые изменения). */
  onBack: () => void;
}

/**
 * Inline-редактор одной витрины (Фаза 8): грузит равый payload (adminGetShowcase),
 * разбирает его в редакторскую модель через parseWireQuizPayload → wireQuizToDraft и
 * показывает визуальный структурированный редактор вопросов (QuizQuestionList — тот
 * же, что в HostEditor). Состояние живёт в useState (НЕ localStorage). На «Сохранить»
 * draft кодируется обратно в wire-payload через JSON.stringify(encodeDraftQuiz(...)) и
 * уходит PUT adminUpdateShowcase → audit showcase.update на сервере. Авторитетная
 * валидация — на сервере (PUT вернёт 400 при ошибке). 409 not_showcase /
 * 400 invalid_payload / 404 → дружелюбные сообщения; успех — inline-notice.
 */
function ShowcaseEditor({ game, onBack }: ShowcaseEditorProps): JSX.Element {
  const { t } = useTranslation();
  const [title, setTitle] = useState(game.title);
  const [draft, setDraft] = useState<QuizDraft | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  // Грузим равый payload витрины и разбираем в редакторскую модель. payload всегда
  // валиден (серверный games.payload), но parseWireQuizPayload может бросить
  // SyntaxError на повреждённой/legacy записи — страхуемся try/catch.
  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setLoadError(null);
    setActionError(null);
    setActionNotice(null);
    adminGetShowcase(game.id)
      .then((d) => {
        if (cancelled) return;
        try {
          const wire = parseWireQuizPayload(d.payload);
          setDraft(wireQuizToDraft(wire, d.title));
          setTitle(d.title);
        } catch (e) {
          if (!cancelled) setLoadError(adminShowcaseError(t, e));
        }
      })
      .catch((e) => {
        if (!cancelled) setLoadError(adminShowcaseError(t, e));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [game.id, t]);

  async function handleSave(): Promise<void> {
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      setActionError(t('admin.showcase.errors.titleEmpty'));
      return;
    }
    setSaving(true);
    setActionError(null);
    setActionNotice(null);
    try {
      const payload = draft ? JSON.stringify(encodeDraftQuiz(draft)) : '{"questions":[]}';
      await adminUpdateShowcase(game.id, { title: trimmedTitle, payload });
      setActionNotice(t('admin.showcase.saved'));
    } catch (e) {
      setActionError(adminShowcaseError(t, e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="block"
      aria-labelledby="admin-showcase-editor-title"
      data-testid="admin-showcase-editor"
    >
      <div className="block-head">
        <div>
          <h3 id="admin-showcase-editor-title" className="block-subtitle">
            {t('admin.showcase.editorTitle')}
          </h3>
          <p className="muted">{t('admin.showcase.editorSubtitle', { title: game.title })}</p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onBack}
          data-testid="admin-showcase-back"
        >
          {t('admin.showcase.back')}
        </button>
      </div>

      {detailLoading ? (
        <p className="muted" data-testid="admin-showcase-editor-loading">
          {t('admin.showcase.loadingDetail')}
        </p>
      ) : loadError ? (
        <p className="error" role="alert" data-testid="admin-showcase-editor-error">
          {loadError}
        </p>
      ) : (
        <>
          <div className="form-row">
            <label className="field-label" htmlFor="showcase-title">
              {t('admin.showcase.fields.title')}
            </label>
            <input
              id="showcase-title"
              className="text-input"
              type="text"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="admin-showcase-field-title"
            />
          </div>

          <div className="form-row" data-testid="admin-showcase-quiz-editor">
            <label className="field-label">
              {t('admin.showcase.fields.payload')}{' '}
              <span className="muted small">
                (
                {t('admin.showcase.payloadBytes', {
                  n: draft ? JSON.stringify(encodeDraftQuiz(draft)).length : 0,
                })}
                )
              </span>
            </label>
            <QuizQuestionList
              questions={draft?.questions ?? []}
              onChange={(qs) =>
                setDraft((d) =>
                  d
                    ? { ...d, questions: qs }
                    : { id: 'showcase', title, questions: qs, updatedAt: Date.now() },
                )
              }
              showPreview={true}
            />
            {!draft || draft.questions.length === 0 ? (
              <p className="muted small">{t('admin.showcase.noQuestions')}</p>
            ) : null}
          </div>

          <div className="btn-row">
            <button
              type="button"
              className="cta"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="admin-showcase-save"
            >
              {saving ? t('common.saving') : t('admin.showcase.save')}
            </button>
          </div>

          {actionError ? (
            <p className="error" role="alert" data-testid="admin-showcase-action-error">
              {actionError}
            </p>
          ) : null}
          {actionNotice ? (
            <p className="muted" role="status" data-testid="admin-showcase-action-notice">
              {actionNotice}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

// --- Вкладка «Картинки» (Фаза 7, И5) ----------------------------------------
//
// Самодостаточный экран модерации загруженных картинок: список файлов UPLOADS_DIR
// с размерами, mtime и referenced-флагом (используется ли квизом), фильтр (поиск по
// имени + статус referenced) + пагинация + удаление. Удаление referenced-картинки
// без ?force → сервер 409 {error:"referenced", games:[ids]}; UI показывает список
// ссылающихся квизов и предлагает «удалить принудительно» (force) с доп. подтверждением
// (квиз покажет битый <img> — RA7). 404/400 — дружелюбные сообщения.

/** Размер страницы списка картинок (пагинация по offset). */
const UPLOADS_PAGE_SIZE = 20;

/** Значения фильтра по referenced-статусу (трёхзначная логика → adminListUploads.referenced). */
type UploadsRefFilter = 'all' | 'used' | 'unused';

/** Полный фильтр списка картинок. */
interface UploadsFilter {
  q: string;
  referenced: UploadsRefFilter;
}

const INITIAL_UPLOADS_FILTER: UploadsFilter = { q: '', referenced: 'all' };

/**
 * Сообщение об ошибке модерации картинок по статусу. 409 referenced — особый случай:
 * он НЕ ошибка в обычном смысле (файл на месте, защищён), а сигнал «подтверди force» —
 * обрабатывается отдельно (conflict-блок), сюда попадают только прочие 409. 404 — файл
 * уже удалён; 400 — недопустимое имя (path-traversal guard); остальное — adminErrorMessage.
 */
function adminUploadErrorMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return t('admin.uploads.errors.not_found');
    if (e.status === 400) return t('admin.uploads.errors.invalid_name');
    if (e.status === 409) return t('admin.uploads.errors.referenced');
  }
  return adminErrorMessage(t, e);
}

/**
 * Извлекает список id ссылающихся квизов из тела 409-ответа
 * ({error:"referenced", games:[...]}). Пустой массив, если тело отсутствует/нечитаемо.
 */
function referencedGamesFromError(e: unknown): number[] {
  if (e instanceof ApiError && e.body && Array.isArray(e.body.games)) {
    return e.body.games.filter((g): g is number => typeof g === 'number');
  }
  return [];
}

/**
 * Самодостаточный экран модерации картинок (И5): таблица файлов UPLOADS_DIR с
 * referenced-бейджем, фильтр (поиск + статус) + пагинация + удаление (force для
 * referenced). Загружает GET /api/admin/uploads при монтировании/смене фильтра.
 * 403 → adminErrorMessage (недостаточно прав); 503 → как network error.
 */
function UploadsView(): JSX.Element {
  const { t } = useTranslation();
  const byteUnits = useByteUnits();
  const fmt = useCallback((n: number): string => formatBytes(n, byteUnits), [byteUnits]);

  const [filter, setFilter] = useState<UploadsFilter>(INITIAL_UPLOADS_FILTER);
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState<AdminUploadItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Удаление: deletingName — имя удаляемого файла (блокирует все delete-кнопки);
  // actionError/actionNotice — inline-сообщения об ошибке/успехе.
  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  // Конфликт referenced: {name, games} когда сервер ответил 409 без force. UI показывает
  // список ссылающихся квизов + кнопку «удалить принудительно». null — конфликта нет.
  const [conflict, setConflict] = useState<{ name: string; games: number[] } | null>(null);

  const loadUploads = useCallback(
    async (f: UploadsFilter, off: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params: AdminListUploadsParams = { limit: UPLOADS_PAGE_SIZE, offset: off };
        const q = f.q.trim();
        if (q.length > 0) params.q = q;
        if (f.referenced === 'used') params.referenced = true;
        else if (f.referenced === 'unused') params.referenced = false;
        const resp = await adminListUploads(params);
        setItems(resp.items);
        setTotal(resp.total);
      } catch (e) {
        setError(adminErrorMessage(t, e));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadUploads(filter, offset);
  }, [loadUploads, filter, offset]);

  function handleApply(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setOffset(0);
    setFilter((prev) => ({ ...prev, q: searchInput }));
  }

  function handleReferencedChange(referenced: UploadsRefFilter): void {
    setOffset(0);
    setFilter((prev) => ({ ...prev, referenced }));
  }

  // delete без force: unreferenced удалится сразу; referenced → 409 → показываем conflict.
  async function handleDelete(item: AdminUploadItem): Promise<void> {
    setConflict(null);
    if (!window.confirm(t('admin.uploads.confirm', { name: item.name }))) return;
    setDeletingName(item.name);
    setActionError(null);
    setActionNotice(null);
    try {
      await adminDeleteUpload(item.name);
      setActionNotice(t('admin.uploads.deleted', { name: item.name }));
      setItems((prev) => prev.filter((x) => x.name !== item.name));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // referenced без force: показываем список квизов + предлагаем force (RA7).
        setConflict({ name: item.name, games: referencedGamesFromError(e) });
      } else {
        setActionError(adminUploadErrorMessage(t, e));
      }
    } finally {
      setDeletingName(null);
    }
  }

  // force-удаление referenced-картинки (осознанное: квиз покажет битый <img>).
  async function handleForceDelete(name: string, games: number[]): Promise<void> {
    const gamesList = games.join(', ');
    if (!window.confirm(t('admin.uploads.confirmForce', { name, games: gamesList }))) return;
    setDeletingName(name);
    setActionError(null);
    try {
      await adminDeleteUpload(name, { force: true });
      setConflict(null);
      setActionNotice(t('admin.uploads.deleted', { name }));
      setItems((prev) => prev.filter((x) => x.name !== name));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      setActionError(adminUploadErrorMessage(t, e));
    } finally {
      setDeletingName(null);
    }
  }

  const page = Math.floor(offset / UPLOADS_PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  // hasNext: текущая страница полная И за ней ещё есть (total > offset+len). Проще:
  // если показано ровно UPLOADS_PAGE_SIZE и total > конца страницы — есть следующая.
  const hasNext = offset + items.length < total;

  return (
    <section className="block" aria-labelledby="admin-uploads-title" data-testid="admin-uploads">
      <div className="block-head">
        <div>
          <h3 id="admin-uploads-title" className="block-subtitle">
            {t('admin.uploads.title')}
          </h3>
          <p className="muted">{t('admin.uploads.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => void loadUploads(filter, offset)}
          disabled={loading}
        >
          {t('common.refresh')}
        </button>
      </div>

      <form className="admin-filters" onSubmit={handleApply}>
        <input
          type="search"
          className="admin-filters__search"
          placeholder={t('admin.uploads.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label={t('admin.uploads.searchPlaceholder')}
          data-testid="admin-uploads-filter-q"
        />
        <select
          className="admin-filters__select"
          value={filter.referenced}
          onChange={(e) => handleReferencedChange(e.target.value as UploadsRefFilter)}
          aria-label={t('admin.uploads.referencedLabel')}
          data-testid="admin-uploads-filter-referenced"
        >
          <option value="all">{t('admin.uploads.referencedAll')}</option>
          <option value="used">{t('admin.uploads.referencedOnly')}</option>
          <option value="unused">{t('admin.uploads.referencedUnused')}</option>
        </select>
        <button type="submit" className="link-btn" disabled={loading}>
          {t('admin.uploads.searchButton')}
        </button>
      </form>

      {loading ? (
        <p className="muted" role="status">
          {t('admin.uploads.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-uploads-error">
          {error}
        </p>
      ) : items.length === 0 ? (
        <p className="muted">{t('admin.uploads.empty')}</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.uploads.colName')}</th>
              <th>{t('admin.uploads.colSize')}</th>
              <th>{t('admin.uploads.colModified')}</th>
              <th>{t('admin.uploads.colStatus')}</th>
              <th>{t('admin.uploads.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.name} data-testid="admin-uploads-row">
                <td className="mono">{u.name}</td>
                <td>{fmt(u.size)}</td>
                <td>{formatDate(u.mtime)}</td>
                <td>
                  {u.referenced ? (
                    <span
                      className="badge badge--used"
                      data-testid="admin-uploads-referenced-badge"
                    >
                      {t('admin.uploads.usedBadge')}
                    </span>
                  ) : (
                    <span className="badge badge--muted">{t('admin.uploads.unusedBadge')}</span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="link-btn link-btn--danger"
                    onClick={() => void handleDelete(u)}
                    disabled={deletingName !== null}
                    data-testid="admin-uploads-delete"
                  >
                    {deletingName === u.name
                      ? t('admin.uploads.deleting')
                      : t('admin.uploads.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {conflict ? (
        <div className="admin-notice admin-notice--warn" data-testid="admin-uploads-conflict">
          <p>{t('admin.uploads.referencedGames', { games: conflict.games.join(', ') })}</p>
          <button
            type="button"
            className="link-btn link-btn--danger"
            onClick={() => void handleForceDelete(conflict.name, conflict.games)}
            disabled={deletingName !== null}
            data-testid="admin-uploads-force"
          >
            {t('admin.uploads.force')}
          </button>
        </div>
      ) : null}

      {actionError ? (
        <p className="error" role="alert" data-testid="admin-uploads-action-error">
          {actionError}
        </p>
      ) : null}
      {actionNotice ? (
        <p className="muted" role="status" data-testid="admin-uploads-action-notice">
          {actionNotice}
        </p>
      ) : null}

      {items.length > 0 || offset > 0 ? (
        <div className="admin-pager">
          <button
            type="button"
            className="link-btn"
            onClick={() => setOffset(Math.max(0, offset - UPLOADS_PAGE_SIZE))}
            disabled={!hasPrev || loading}
          >
            {t('admin.uploads.prev')}
          </button>
          <span className="muted">{t('admin.uploads.pageInfo', { page })}</span>
          <button
            type="button"
            className="link-btn"
            onClick={() => setOffset(offset + UPLOADS_PAGE_SIZE)}
            disabled={!hasNext || loading}
          >
            {t('admin.uploads.next')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ─── Вкладка «Лимиты» (Фаза 7, И6) ──────────────────────────────────────────

/**
 * Группа лимита для визуальной группировки в форме вкладки «Лимиты». Соответствует
 * семантике: content — что в квизе (вопросы/картинки/символы), room — комната/
 * сессия (игроки/параллельные комнаты), account — аккаунт ведущего (квизов).
 */
type LimitGroup = 'content' | 'room' | 'account';

/** Конфиг одного поля лимита: ключ settings + группа + признак «байты» (КБ-юнит). */
interface LimitFieldConfig {
  /** Ключ settings (limits.*). */
  key: string;
  /** Группа для рендера. */
  group: LimitGroup;
  /** true → значение в байтах (показываем formatBytes, юнит «КБ»). */
  isBytes?: boolean;
}

/**
 * Конфиг всех 12 полей в стабильном порядке (для детерминированного рендера).
 * Группировка: content (вопросы/размер/символы/открытые квизы — план §C7.4),
 * room (игроки/комнаты/темп авто-перехода), account (квизов на хоста). subkey
 * для i18n = key без префикса `limits.`. GET /api/admin/limits отдаёт все ключи
 * автоматически (limitMetas на сервере); фронт фильтрует рендер по этому списку.
 */
const LIMIT_FIELDS: LimitFieldConfig[] = [
  { key: LIMIT_KEYS.quizzesPerHost, group: 'account' },
  { key: LIMIT_KEYS.questionsPerQuiz, group: 'content' },
  { key: LIMIT_KEYS.quizImageTotalBytes, group: 'content', isBytes: true },
  { key: LIMIT_KEYS.charQuizTitle, group: 'content' },
  { key: LIMIT_KEYS.charQuestion, group: 'content' },
  { key: LIMIT_KEYS.charOption, group: 'content' },
  { key: LIMIT_KEYS.charOpenAnswer, group: 'content' },
  { key: LIMIT_KEYS.codeMaxChars, group: 'content' },
  { key: LIMIT_KEYS.openQuizzesPerUser, group: 'content' },
  { key: LIMIT_KEYS.openQuizMaxAttempts, group: 'content' },
  { key: LIMIT_KEYS.playersPerRoom, group: 'room' },
  { key: LIMIT_KEYS.parallelRooms, group: 'room' },
  { key: LIMIT_KEYS.roomIdleTTLMinutes, group: 'room' },
  { key: LIMIT_KEYS.autoNextDelaySeconds, group: 'room' },
];

/** Под-ключ лимита для i18n (limits.quizzes_per_host → quizzes_per_host). */
function limitSubkey(key: string): string {
  return key.startsWith('limits.') ? key.slice('limits.'.length) : key;
}

/** i18n-ключ человекочитаемой метки группы. */
const GROUP_LABEL: Record<LimitGroup, string> = {
  content: 'admin.limits.groupContent',
  room: 'admin.limits.groupRoom',
  account: 'admin.limits.groupAccount',
};

/** Порядок групп в форме (для секций). */
const GROUP_ORDER: LimitGroup[] = ['account', 'content', 'room'];

/**
 * LimitsView — просмотр и редактирование конфигурируемых лимитов (GET/PUT
 * /api/admin/limits). Грузит эффективные значения + метаданные (default/min/max),
 * рендерит форму с инпутами (number), сгруппированную по content/room/account.
 *
 * - Человекочитаемые единицы: байты → «КБ» (formatBytes), показ дефолта рядом
 *   («по умолчанию: N»), пометка «изменено» если value != default.
 * - «Сохранить» → PUT только изменившихся ключей (как строки-числа) → success;
 *   400 (невалидное/out of range) → дружелюбное сообщение с диапазоном.
 * - «Сбросить» у поля → возвращает draft к default (локально, без отдельного
 *   запроса; попадёт в Save если отличается от текущего effective).
 * - Аудит (limits.update) пишется на сервере в той же tx; инвалидирует кеш.
 */
function LimitsView(): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<AdminLimitItem[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  // По ключу → метаданные (default/min/max/description) для валидации и подсказок.
  const metaByKey = useMemo(() => {
    const m: Record<string, AdminLimitItem> = {};
    for (const it of items) m[it.key] = it;
    return m;
  }, [items]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActionError(null);
    setActionNotice(null);
    adminGetLimits()
      .then((resp: AdminLimitsResp) => {
        if (cancelled) return;
        setItems(resp.limits);
        const d: Record<string, string> = {};
        for (const it of resp.limits) d[it.key] = it.value;
        setDraft(d);
      })
      .catch((e) => {
        if (!cancelled) setError(adminErrorMessage(t, e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  /** Изменилось ли поле относительно effective-значения (для подсветки Save). */
  function isChanged(key: string): boolean {
    const cur = metaByKey[key]?.value;
    return cur !== undefined && draft[key] !== cur;
  }
  const hasChanges = LIMIT_FIELDS.some((f) => isChanged(f.key));

  /** value отличается от default → бейдж «изменено». */
  function isModified(key: string): boolean {
    const def = metaByKey[key]?.default;
    return def !== undefined && draft[key] !== def;
  }

  function handleChange(key: string, value: string): void {
    setActionNotice(null);
    setActionError(null);
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function handleReset(key: string): void {
    const def = metaByKey[key]?.default;
    if (def === undefined) return;
    setActionNotice(null);
    setActionError(null);
    setDraft((prev) => ({ ...prev, [key]: def }));
  }

  async function handleSave(): Promise<void> {
    const changes: Record<string, string> = {};
    for (const f of LIMIT_FIELDS) {
      if (isChanged(f.key)) changes[f.key] = draft[f.key];
    }
    if (Object.keys(changes).length === 0) {
      setActionNotice(t('admin.limits.noChanges'));
      return;
    }
    setSaving(true);
    setActionError(null);
    setActionNotice(null);
    try {
      const resp = await adminUpdateLimits(changes);
      setItems(resp.limits);
      const d: Record<string, string> = {};
      for (const it of resp.limits) d[it.key] = it.value;
      setDraft(d);
      setActionNotice(t('admin.limits.saved'));
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        // Сервер отдаёт код в {"error":...} (unknown/not integer/out of range).
        // Для out of range показываем диапазон; иначе — общий invalid-текст.
        setActionError(t('admin.limits.errors.invalid', { field: '', min: 0, max: 0 }));
      } else {
        setActionError(adminErrorMessage(t, e));
      }
    } finally {
      setSaving(false);
    }
  }

  /** Юнит поля для подписи инпута («КБ» для байтов, иначе пусто). */
  function unitLabel(f: LimitFieldConfig): string {
    if (f.isBytes) return t('admin.limits.unitBytes');
    return '';
  }

  return (
    <section className="block" aria-labelledby="admin-limits-title" data-testid="admin-limits">
      <div className="block-head">
        <div>
          <h3 id="admin-limits-title" className="block-subtitle">
            {t('admin.limits.title')}
          </h3>
          <p className="muted">{t('admin.limits.subtitle')}</p>
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            setLoading(true);
            adminGetLimits()
              .then((resp) => {
                setItems(resp.limits);
                const d: Record<string, string> = {};
                for (const it of resp.limits) d[it.key] = it.value;
                setDraft(d);
              })
              .catch((e) => setError(adminErrorMessage(t, e)))
              .finally(() => setLoading(false));
          }}
          disabled={loading || saving}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading ? (
        <p className="muted" data-testid="admin-limits-loading">
          {t('admin.limits.loading')}
        </p>
      ) : error ? (
        <p className="error" role="alert" data-testid="admin-limits-error">
          {error}
        </p>
      ) : items.length === 0 ? (
        <p className="muted">{t('admin.limits.empty')}</p>
      ) : (
        GROUP_ORDER.map((group) => {
          const fields = LIMIT_FIELDS.filter((f) => f.group === group);
          if (fields.length === 0) return null;
          return (
            <div
              key={group}
              className="admin-limits__group"
              data-testid={`admin-limits-group-${group}`}
            >
              <h4 className="block-subtitle admin-limits__group-title">{t(GROUP_LABEL[group])}</h4>
              <div className="admin-limits__rows">
                {fields.map((f) => {
                  const sub = limitSubkey(f.key);
                  const meta = metaByKey[f.key];
                  const val = draft[f.key] ?? '';
                  const min = meta?.min ?? 0;
                  const max = meta?.max ?? 0;
                  const modified = isModified(f.key);
                  return (
                    <div className="admin-limits__row" key={f.key} data-testid="admin-limits-row">
                      <div className="admin-limits__field">
                        <label className="field-label" htmlFor={`limit-${sub}`}>
                          {t(`admin.limits.field.${sub}`)}
                          {modified ? (
                            <span
                              className="badge badge--muted admin-limits__badge"
                              data-testid={`admin-limits-modified-${sub}`}
                            >
                              {t('admin.limits.modifiedBadge')}
                            </span>
                          ) : null}
                        </label>
                        <p className="muted small">{t(`admin.limits.desc.${sub}`)}</p>
                      </div>
                      <div className="admin-limits__input">
                        <input
                          id={`limit-${sub}`}
                          className="text-input admin-limits__value"
                          type="number"
                          inputMode="numeric"
                          min={min}
                          max={max}
                          value={val}
                          onChange={(e) => handleChange(f.key, e.target.value)}
                          data-testid={`admin-limits-input-${sub}`}
                        />
                        {unitLabel(f).length > 0 ? (
                          <span className="muted small admin-limits__unit">{unitLabel(f)}</span>
                        ) : null}
                        {f.isBytes ? (
                          <span
                            className="muted small admin-limits__bytes"
                            data-testid={`admin-limits-kb-${sub}`}
                          >
                            {formatBytes(Number.parseInt(val, 10) || 0)}
                          </span>
                        ) : null}
                      </div>
                      <div className="admin-limits__meta">
                        <span className="muted small">
                          {t('admin.limits.defaultHint', {
                            value: f.isBytes
                              ? formatBytes(Number.parseInt(meta?.default ?? '0', 10))
                              : (meta?.default ?? ''),
                          })}
                        </span>
                        <br />
                        <span className="muted small">
                          {t('admin.limits.rangeHint', { min, max })}
                        </span>
                        <br />
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => handleReset(f.key)}
                          disabled={saving}
                          data-testid={`admin-limits-reset-${sub}`}
                        >
                          {t('admin.limits.reset')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {actionError ? (
        <p className="error" role="alert" data-testid="admin-limits-action-error">
          {actionError}
        </p>
      ) : null}
      {actionNotice ? (
        <p className="muted" role="status" data-testid="admin-limits-action-notice">
          {actionNotice}
        </p>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="editor-actions">
          <button
            type="button"
            className="cta"
            onClick={() => void handleSave()}
            disabled={saving || !hasChanges}
            data-testid="admin-limits-save"
          >
            {saving ? t('admin.limits.saving') : t('admin.limits.save')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
